import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ComponentPropsWithoutRef, ComponentType, forwardRef, HTMLAttributes } from 'react';

import { ReactComponent as AnimatedSpinningCircle } from '@/assets/icons/animated-spinning-circle.svg';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import { Badge, BadgeVariant } from '../Badge/Badge';
import { Icon } from '../Icon';

const iconClassName = 'tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6';

type IconComponent = ComponentType<ComponentPropsWithoutRef<'svg'> & { title?: string }>;
type IndicatorIconVariant = 'destructive' | 'warning' | 'success';

export interface ValidationStateIndicatorProps extends HTMLAttributes<HTMLDivElement> {
    icon: IconComponent;
    iconVariant: IndicatorIconVariant;
    title?: string;
    /** When provided and > 0, a count badge is rendered next to the icon (hidden < lg, like the header). */
    count?: number;
    badgeVariant?: BadgeVariant;
}

/**
 * Single validation icon paired with an optional count badge — the visual
 * "chip" used by the accordion section `SectionValidationPopover`. Kept as a
 * `forwardRef` so it can be used as a Radix Popover trigger via `asChild`.
 *
 * The wrapper uses `tw-items-start` (not `-center`) so the count badge lines
 * up with the top of the icon, matching the Figma spec.
 */
export const ValidationStateIndicator = forwardRef<HTMLDivElement, ValidationStateIndicatorProps>(
    ({ icon, iconVariant, title, count, badgeVariant, className, ...rest }, ref) => (
        <div ref={ref} className={cn('tw-flex tw-items-start', className)} {...rest}>
            <Icon
                icon={icon}
                variant={iconVariant}
                size="sm"
                className={iconClassName}
                title={title}
            />
            {count !== undefined && count > 0 && badgeVariant && (
                <Badge type="count" variant={badgeVariant} className="tw-hidden lg:tw-inline-flex">
                    {count}
                </Badge>
            )}
        </div>
    ),
);
ValidationStateIndicator.displayName = 'ValidationStateIndicator';

export interface ValidationStateProps {
    hasWarnings?: boolean;
    isCompliant?: boolean;
    isDirty?: boolean;
    /**
     * `inline` — legacy single-icon priority (grid cells).
     * `header` — Figma editor header: parallel error/warning icons (no counts).
     */
    layout?: 'inline' | 'header';
}

const ValidationStateInline = ({
    hasWarnings,
    isCompliant,
    isDirty,
}: Pick<ValidationStateProps, 'hasWarnings' | 'isCompliant' | 'isDirty'>) => {
    const { t } = useTranslation();
    const conditions = [
        {
            show: !isDirty && hasWarnings,
            icon: ExclamationCircleIcon,
            variant: 'warning' as const,
            key: 'warning',
            tooltip: t('VALIDATION_WARNINGS'),
        },
        {
            show: !isDirty && isCompliant,
            icon: CheckCircleIcon,
            variant: 'success' as const,
            key: 'success',
            tooltip: t('VALIDATION_COMPLIANT'),
        },
        {
            show: !isDirty && !isCompliant,
            icon: XCircleIcon,
            variant: 'destructive' as const,
            key: 'destructive',
            tooltip: t('VALIDATION_NON_COMPLIANT'),
        },
        {
            show: isDirty,
            icon: AnimatedSpinningCircle,
            variant: undefined,
            key: 'loading',
            tooltip: t('VALIDATION_NON_COMPLIANT'),
        },
    ];

    const iconsToRender = conditions
        .filter((item) => item.show)
        .map(({ icon, variant, key, tooltip }) => (
            <Icon
                key={key}
                icon={icon}
                variant={variant}
                size="sm"
                className={iconClassName}
                title={tooltip}
            />
        ));

    if (iconsToRender.length === 0) {
        return null;
    }

    return <>{iconsToRender}</>;
};

const ValidationStateHeader = ({
    hasWarnings,
    isCompliant,
    isDirty,
}: Required<Pick<ValidationStateProps, 'hasWarnings' | 'isCompliant' | 'isDirty'>>) => {
    const { t } = useTranslation();

    if (isDirty) {
        return (
            <Icon
                icon={AnimatedSpinningCircle}
                size="sm"
                className={iconClassName}
                title={t('VALIDATION_NON_COMPLIANT')}
            />
        );
    }

    return (
        <div className="tw-flex tw-items-start lg:tw-gap-1">
            {isCompliant ? (
                <Icon
                    icon={CheckCircleIcon}
                    variant="success"
                    size="sm"
                    className={iconClassName}
                    title={t('VALIDATION_COMPLIANT')}
                />
            ) : (
                <Icon
                    icon={XCircleIcon}
                    variant="destructive"
                    size="sm"
                    className={iconClassName}
                    title={t('VALIDATION_NON_COMPLIANT')}
                />
            )}
            {hasWarnings && (
                <Icon
                    icon={ExclamationCircleIcon}
                    variant="warning"
                    size="sm"
                    className={iconClassName}
                    title={t('VALIDATION_WARNINGS')}
                />
            )}
        </div>
    );
};

export const ValidationState = ({
    hasWarnings,
    isCompliant,
    isDirty,
    layout = 'inline',
}: ValidationStateProps) => {
    if (layout === 'header') {
        return (
            <ValidationStateHeader
                hasWarnings={!!hasWarnings}
                isCompliant={!!isCompliant}
                isDirty={!!isDirty}
            />
        );
    }

    return (
        <ValidationStateInline
            hasWarnings={hasWarnings}
            isCompliant={isCompliant}
            isDirty={isDirty}
        />
    );
};
